import React, { useState, useRef, useEffect } from "react";
import { FaPause, FaPlay } from "react-icons/fa";

const CustomAudioPill = ({ audioUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // Number of bars in the waveform
  const barCount = 40;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleWaveformClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Generate bar heights with variation
  const generateBarHeights = () => {
    return Array.from({ length: barCount }, (_, i) => {
      const base = 30 + Math.sin(i * 0.5) * 20;
      const variation = Math.random() * 20;
      return base + variation;
    });
  };

  const [barHeights] = useState(generateBarHeights());

  return (
    <div className="audio-pill-container">
      <audio ref={audioRef} src={audioUrl} />

      {/* Play/Pause Button */}
      <button
        onClick={togglePlayPause}
        className="audio-pill-button"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <FaPause className="audio-pill-icon" fill="currentColor" />
        ) : (
          <FaPlay className="audio-pill-icon audio-play-icon" fill="currentColor" />
        )}
      </button>

      {/* Waveform */}
      <div className="audio-pill-waveform" onClick={handleWaveformClick}>
        {barHeights.map((height, index) => {
          const progress = duration > 0 ? currentTime / duration : 0;
          const barProgress = index / barCount;
          const isPassed = barProgress <= progress;
          const animationDelay = index * 0.05;

          return (
            <div
              key={index}
              className={`audio-pill-bar ${isPassed ? "passed" : ""} ${
                isPlaying ? "playing" : ""
              }`}
              style={{
                height: isPlaying ? `${height}%` : "60%",
                animationDelay: `${animationDelay}s`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
export default CustomAudioPill;
